import { FC, RefObject, useRef, useState, useEffect } from "react";
import { ViewProps } from "../components/SliderType/components/PopupSlider/types";
import { groupMenuItemsByCategory } from "../utils/groupMenuItemsByCategory";
import { CategoryGroup } from "@/features/MenuPage/types";
import { useBottomTabs } from "@/context/BottomTabsContext";
import { TabItem } from "@/components/lheader/components/BottomTabs";

export type WithActionsProps = {
  isModalOpen: boolean;
  handleImageClick: (index: number) => void;
  handleCloseModal: () => void;
  selectedImageIndex: number;
  canScrollLeft: boolean;
  canScrollRight: boolean;
  thumbnailsContainerRef: RefObject<HTMLDivElement>;
  firstThumbnailRef: RefObject<HTMLDivElement>;
  slidePrevThumbnails: () => void;
  slideNextThumbnails: () => void;
  updateScrollButtons: () => void;
  setSelectedImageIndex: (index: number) => void;
  groupedProducts?: CategoryGroup[];
  selectedItem: any;
  setSelectedItem: (item: any) => void;
};

export const withActions =
  (View: FC<WithActionsProps>) => (props: ViewProps) => {
    const { menu, products, groupByCategory } = props;
    const groupedProducts = groupMenuItemsByCategory(
      menu?.data,
      products?.data
    );

    const { setTabs, setSelectedTabId } = useBottomTabs();

    const [selectedImageIndex, setSelectedImageIndex] = useState(0);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(true);
    const [selectedItem, setSelectedItem] = useState<any>(null);

    const thumbnailsContainerRef = useRef<HTMLDivElement>(null);
    const firstThumbnailRef = useRef<HTMLDivElement>(null);

    const thumbnailsPerRow = 3;

    const handleImageClick = (index: number) => {
      setSelectedImageIndex(index);
      setIsModalOpen(true);
    };

    const handleCloseModal = () => {
      setIsModalOpen(false);
    };

    const getThumbnailWidth = () => {
      if (firstThumbnailRef.current) {
        const style = window.getComputedStyle(firstThumbnailRef.current);
        const width = firstThumbnailRef.current.clientWidth;
        const marginRight = parseFloat(style.marginRight);
        return width + marginRight;
      }
      return 0;
    };

    const updateScrollButtons = () => {
      if (thumbnailsContainerRef.current) {
        const { scrollLeft, scrollWidth, clientWidth } =
          thumbnailsContainerRef.current;
        setCanScrollLeft(scrollLeft > 0);
        setCanScrollRight(scrollLeft + clientWidth < scrollWidth);
      }
    };

    const slideNextThumbnails = () => {
      if (thumbnailsContainerRef.current) {
        const scrollAmount = getThumbnailWidth() * thumbnailsPerRow;
        thumbnailsContainerRef.current.scrollBy({
          left: scrollAmount,
          behavior: "smooth",
        });
        setTimeout(updateScrollButtons, 500);
      }
    };

    const slidePrevThumbnails = () => {
      if (thumbnailsContainerRef.current) {
        const scrollAmount = getThumbnailWidth() * thumbnailsPerRow;
        thumbnailsContainerRef.current.scrollBy({
          left: -scrollAmount,
          behavior: "smooth",
        });
        setTimeout(updateScrollButtons, 500);
      }
    };

    // Update the tabs in the context when groupedProducts changes
    useEffect(() => {
      // Skip if no products
      if (!groupByCategory || !groupedProducts?.length) {
        return;
      }

      // Convert CategoryGroup[] to TabItem[]
      const tabItems = groupedProducts.map((category) => ({
        id: category.id.toString(),
        name: category.name,
      }));

      // Update context - MERGE with existing tabs instead of replacing
      setTabs((prevTabs: TabItem[]) => {
        // Create a map of existing tab IDs
        const existingTabIds = new Set(prevTabs.map((tab) => tab.id));

        // Filter out new tabs that already exist
        const newTabs = tabItems.filter((tab) => !existingTabIds.has(tab.id));

        // Merge previous and new tabs
        const mergedTabs = [...prevTabs, ...newTabs];

        // If we have new tabs and no selection yet, set the first tab as selected
        if (newTabs.length > 0 && !prevTabs.length) {
          setSelectedTabId(mergedTabs[0].id);
        }

        return mergedTabs;
      });

      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [groupByCategory, menu?.data?.id, products?.data?.length]);

    return (
      <View
        {...props}
        isModalOpen={isModalOpen}
        handleImageClick={handleImageClick}
        handleCloseModal={handleCloseModal}
        selectedImageIndex={selectedImageIndex}
        canScrollLeft={canScrollLeft}
        canScrollRight={canScrollRight}
        thumbnailsContainerRef={thumbnailsContainerRef}
        firstThumbnailRef={firstThumbnailRef}
        slidePrevThumbnails={slidePrevThumbnails}
        slideNextThumbnails={slideNextThumbnails}
        updateScrollButtons={updateScrollButtons}
        setSelectedImageIndex={setSelectedImageIndex}
        groupedProducts={groupedProducts}
        selectedItem={selectedItem}
        setSelectedItem={setSelectedItem}
      />
    );
  };
